import {
  clearReference,
  readReference,
  writeReference,
} from './referenceStore';
import { bindReference } from '@/api/order/bindReference';
import { REFERENCE_MAX_BIND_ATTEMPTS } from '@/config/reference';

/** Guards against two navigations firing a bind before the first resolves. */
let inFlight = false;

/**
 * Attaches a captured reference to a result server-side, once both are known.
 *
 * Idempotent and safe to call on every navigation: it no-ops unless there is a stored reference
 * whose boundTo does not already match this result.
 */
export async function bindReferenceIfNeeded(
  resultId: string | null | undefined
) {
  if (!resultId || inFlight) return;

  const record = readReference();
  if (!record) return;
  if (record.boundTo === resultId) return;
  if (record.attempts >= REFERENCE_MAX_BIND_ATTEMPTS) return;

  inFlight = true;

  try {
    const response = await bindReference({
      result_id: resultId,
      reference_id: record.referenceId,
    });

    if (response.data.meta?.reference_bound_to_user) {
      // Durable server-side: the API inherits it through any re-mint, so holding a browser copy
      // from here only adds mis-attribution risk.
      clearReference();
    } else {
      // Still anonymous, so keep buffering — an address change mints a new result and the client
      // is the only route back.
      writeReference({ ...record, boundTo: resultId, attempts: 0 });
    }
  } catch (error) {
    // boundTo stays unset on failure, so without this counter an erroring bind would POST on
    // every navigation for the rest of the session.
    const attempts = record.attempts + 1;
    writeReference({ ...record, attempts });

    if (attempts >= REFERENCE_MAX_BIND_ATTEMPTS) {
      reportError(
        error instanceof Error
          ? error
          : new Error('Reference bind failed; giving up')
      );
    }
  } finally {
    inFlight = false;
  }
}
